import { useEffect, useState } from "react";
import { Loader2, Users, LogIn, LogOut as LogOutIcon, Clock3, UserX, Building2, Activity } from "lucide-react";
import { staffApi, fmtTime, fmtErr } from "../api";

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
    <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className="mt-2 font-display text-3xl font-extrabold text-slate-900">{value ?? "—"}</p>
  </div>
);

const AdminHome = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await staffApi.get("/monitor/live");
        setData(data);
      } catch (e) {
        setError(fmtErr(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading live dashboard…</p>;
  if (error) return <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="admin-home-error">{error}</p>;

  const feed = Array.isArray(data?.activity_feed) ? data.activity_feed : [];
  const branches = Array.isArray(data?.per_branch) ? data.per_branch : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-2xl font-bold text-slate-900">Admin Overview</h1>
      <p className="mt-1 text-sm text-slate-500">Live workforce status{data?.generated_at ? ` · updated ${fmtTime(data.generated_at)}` : ""}.</p>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat label="Employees" value={data?.total_employees} testid="stat-total" />
        <Stat label="Checked In Today" value={data?.checked_in_today} testid="stat-checked-in" />
        <Stat label="Inside Now" value={data?.inside_count} testid="stat-inside" />
        <Stat label="Outside Now" value={data?.outside_count} testid="stat-outside" />
        <Stat label="Late Today" value={data?.late_today} testid="stat-late" />
        <Stat label="Absent Today" value={data?.absent_today} testid="stat-absent" />
      </div>
      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 p-6" data-testid="admin-branch-summary">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            <Building2 size={14} className="text-[#C5221F]" /> Branch Summary
          </p>
          {branches.length ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {branches.map((b, i) => (
                <li key={b.branch_id || b.branch_name || i} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-slate-800">{b.branch_name || b.name || "Branch"}</span>
                  <span className="text-xs text-slate-500">
                    {Object.entries(b).filter(([k]) => !["branch_id", "branch_name", "name"].includes(k)).map(([k, v]) => `${k.replaceAll("_", " ")}: ${v}`).join(" · ") || "—"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-slate-500">No branch data available.</p>
          )}
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="admin-activity-feed">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            <Activity size={14} className="text-[#C5221F]" /> Recent Activity
          </p>
          {feed.length ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {feed.slice(0, 12).map((f, i) => (
                <li key={i} className="py-2.5 text-sm text-slate-700 flex items-center gap-2">
                  {(f.type || "").toLowerCase().includes("out") ? <LogOutIcon size={13} className="text-slate-400 shrink-0" /> : <LogIn size={13} className="text-emerald-600 shrink-0" />}
                  <span className="font-medium">{f.user_name || f.name || "Employee"}</span>
                  <span className="text-xs text-slate-400">{f.type || f.event_type || ""} {f.time || f.timestamp ? `· ${fmtTime(f.time || f.timestamp)}` : ""}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-slate-500">No recent activity.</p>
          )}
          <p className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-400 flex items-center gap-4">
            <span className="inline-flex items-center gap-1"><Users size={12} /> {data?.recent_entries?.length ?? 0} recent entries</span>
            <span className="inline-flex items-center gap-1"><UserX size={12} /> {data?.recent_exits?.length ?? 0} recent exits</span>
            <span className="inline-flex items-center gap-1"><Clock3 size={12} /> live</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
